import { Text, type TextProps } from "@/components/Typography";
import { type ReactNode, useMemo } from "react";
/**
 * MarkdownText コンポーネント
 *
 * 【暫定配置について】
 * このコンポーネントは、マークダウン形式を取り扱う機能要件が固まっていないため、
 * 暫定的に `src/features/form-factory/components/fields/` 配下に配置しています。
 *
 * 暫定配置の理由：
 * 1. マークダウン機能の要件が未確定
 *    - リッチテキストエディタの選定や実装方針が決まっていない
 *    - マークダウンのサポート範囲（どの記法まで対応するか）が未決定
 *
 * 2. 実装方針の選定が必要
 *    - 自作実装 vs ライブラリ利用の技術選定が未完了
 *    - パフォーマンスやセキュリティ面での考慮が必要
 *
 * 3. 影響範囲の限定
 *    - 現在は form-factory の helpText でのみ利用
 *    - 他の機能への影響を避けるため、機能内に閉じて実装
 */

interface MarkdownTextProps extends TextProps {
  /** マークダウン記法をレンダリングする文字列 */
  children: string;
}

/**
 * マークダウン記法の種類を定義
 */
type MarkdownElementType = "text" | "link";

/**
 * パースされたマークダウン要素の型
 */
interface ParsedMarkdownElement {
  type: MarkdownElementType;
  content: string;
  startIndex: number;
  endIndex: number;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
  data?: Record<string, any>;
}

/**
 * マークダウンパーサーの型定義
 */
interface MarkdownParser {
  pattern: RegExp;
  type: MarkdownElementType;
  parse: (
    match: RegExpMatchArray,
  ) => Omit<ParsedMarkdownElement, "startIndex" | "endIndex">;
}

/**
 * リンクのデフォルトスタイリングクラス
 */
const DEFAULT_LINK_CLASSES =
  "text-primary-on-neutral-fill underline hover:text-primary-hover-on-neutral-fill";

/**
 * リンク要素を生成するコンポーネント
 */
function LinkElement({
  url,
  displayText,
  keyProp,
}: {
  url: string;
  displayText: string;
  keyProp: string;
}) {
  return (
    <a
      key={keyProp}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={DEFAULT_LINK_CLASSES}
    >
      {displayText}
    </a>
  );
}

/**
 * リンク用のマークダウンパーサー
 */
const linkParser: MarkdownParser = {
  pattern: /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
  type: "link",
  parse: (match) => ({
    type: "link",
    content: match[1], // 表示テキスト
    data: {
      url: match[2], // URL
      displayText: match[1],
    },
  }),
};

/**
 * 利用可能なマークダウンパーサーのリスト
 */
const MARKDOWN_PARSERS: MarkdownParser[] = [linkParser];

/**
 * テキストから全てのマークダウン要素を検出・パースする
 * @param text - 解析対象のテキスト
 * @returns パースされたマークダウン要素の配列（位置順にソート済み）
 */
function findAllMarkdownElements(text: string): ParsedMarkdownElement[] {
  const elements: ParsedMarkdownElement[] = [];

  for (const parser of MARKDOWN_PARSERS) {
    for (const match of text.matchAll(parser.pattern)) {
      const startIndex = match.index ?? 0;
      const endIndex = startIndex + match[0].length;

      const parsedElement = parser.parse(match);
      elements.push({
        ...parsedElement,
        startIndex,
        endIndex,
      });
    }
  }

  // 位置順にソート
  return elements.sort((a, b) => a.startIndex - b.startIndex);
}

/**
 * マークダウン要素をReact要素に変換する
 * @param element - 変換対象の要素
 * @param key - React key
 * @returns 対応するReact要素
 */
function renderMarkdownElement(element: ParsedMarkdownElement, key: string) {
  switch (element.type) {
    case "link":
      return (
        <LinkElement
          key={key}
          url={element.data?.url ?? ""}
          displayText={element.data?.displayText ?? ""}
          keyProp={key}
        />
      );

    case "text":
    default:
      return <span key={key}>{element.content}</span>;
  }
}

/**
 * マークダウンテキストを解析してReact要素配列に変換する
 * @param text - 解析対象のテキスト
 * @returns React要素の配列（テキスト部分とマークダウン要素）
 */
function parseMarkdownToElements(text: string) {
  const markdownElements = findAllMarkdownElements(text);
  const elements: ReactNode[] = [];
  let lastIndex = 0;
  let elementIndex = 0;

  for (const element of markdownElements) {
    // マークダウン要素の前のテキスト部分を追加
    if (element.startIndex > lastIndex) {
      const textPart = text.slice(lastIndex, element.startIndex);
      elements.push(
        renderMarkdownElement(
          {
            type: "text",
            content: textPart,
            startIndex: lastIndex,
            endIndex: element.startIndex,
          },
          `text-${elementIndex++}`,
        ),
      );
    }

    // マークダウン要素を追加
    elements.push(
      renderMarkdownElement(element, `${element.type}-${elementIndex++}`),
    );

    lastIndex = element.endIndex;
  }

  // 最後の残りテキスト部分を追加
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    elements.push(
      renderMarkdownElement(
        {
          type: "text",
          content: remainingText,
          startIndex: lastIndex,
          endIndex: text.length,
        },
        `text-${elementIndex}`,
      ),
    );
  }

  return elements;
}

/**
 * マークダウン記法をサポートしたTextコンポーネント
 *
 * 現在サポートしている記法:
 * - マークダウン記法のリンク: [表示テキスト](URL)
 *
 * @example
 * <MarkdownText variant="labelSmall">
 *   詳細は[こちらのドキュメント](https://example.com/docs)をご確認ください。
 * </MarkdownText>
 */
export function MarkdownText({ children, ...textProps }: MarkdownTextProps) {
  const renderedContent = useMemo(
    () => parseMarkdownToElements(children),
    [children],
  );

  return <Text {...textProps}>{renderedContent}</Text>;
}
